import { useEffect, useRef, useState } from "react";
import AdminLayout from "../AdminLayout";
import ItemsEditor, { type EditorItem, type ItemField } from "../ItemsEditor";
import { newItemId } from "@/lib/ids";
import { API_BASE } from "@/config/api";
import { uploadMedia } from "../uploadMedia";
import {
  DEFAULT_BENEFITS_SECTION,
  effectiveImageFit,
  mergeBenefitsSection,
  type BenefitsSectionConfig,
} from "@/lib/benefitsSectionConfig";
import { normalizeMediaUrl } from "@/lib/mediaUrl";

const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
const labelCls = "block text-xs font-medium text-foreground-600 mb-1";

const BENEFIT_FIELDS: ItemField[] = [
  { key: "titulo", label: "Título" },
  { key: "descripcion", label: "Descripción", type: "textarea" },
  {
    key: "icono",
    label: "Ícono",
    hint: "Nombre del ícono de Remix Icon, por ejemplo ri-truck-line o ri-shield-check-line. Búscalos en remixicon.com.",
  },
];
const STAT_FIELDS: ItemField[] = [
  { key: "valor", label: "Número o valor (ej. 12 m²)" },
  { key: "etiqueta", label: "Texto debajo (ej. Desde)" },
];

async function saveBlock(block: string, items: unknown[]): Promise<boolean> {
  const res = await fetch(`${API_BASE}/content-blocks/home/${block}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  return res.ok;
}

export default function AdminHomeRecinto() {
  const [cfg, setCfg] = useState<BenefitsSectionConfig>(DEFAULT_BENEFITS_SECTION);
  const [benefits, setBenefits] = useState<EditorItem[]>([]);
  const [stats, setStats] = useState<EditorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch(`${API_BASE}/bootstrap`)
      .then((res) => res.json())
      .then((json) => {
        const home = json.contentBlocks?.home;
        setCfg(mergeBenefitsSection(home?.benefits_section?.[0]));
        setBenefits(home?.benefits ?? []);
        setStats(home?.stats ?? []);
      })
      .finally(() => setLoading(false));
  }, []);

  function set<K extends keyof BenefitsSectionConfig>(key: K, value: string) {
    setCfg((c) => ({ ...c, [key]: value }));
  }

  async function handleFile(file: File) {
    setUploading(true);
    setMessage(null);
    try {
      const { url } = await uploadMedia(file, cfg.imagen_alt || "Foto de la sección El recinto");
      set("imagen_url", url);
    } catch (err) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : "No se pudo subir la imagen" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSave() {
    setMessage(null);
    if (!cfg.titulo.trim()) {
      setMessage({ type: "error", text: "El título del encabezado no puede quedar vacío." });
      return;
    }
    const sinTitulo = benefits.findIndex((b) => !(b.titulo ?? "").trim());
    if (sinTitulo >= 0) {
      setMessage({ type: "error", text: `El ítem con ícono ${sinTitulo + 1} necesita un título.` });
      return;
    }
    const sinValor = stats.findIndex((s) => !(s.valor ?? "").trim());
    if (sinValor >= 0) {
      setMessage({ type: "error", text: `El número destacado ${sinValor + 1} necesita su valor.` });
      return;
    }
    setSaving(true);
    try {
      const results = await Promise.all([
        saveBlock("benefits_section", [{ id: "config", ...cfg }]),
        saveBlock("benefits", benefits),
        saveBlock("stats", stats),
      ]);
      if (results.some((ok) => !ok)) {
        setMessage({ type: "error", text: "No se pudo guardar todo. Revisa tu conexión e inténtalo de nuevo." });
        return;
      }
      setMessage({ type: "success", text: "Sección guardada. Los cambios ya se ven en la página de inicio." });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Sección «El recinto» (inicio)</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Es la sección oscura de la página de inicio. Aquí editas todo lo que se ve en ella: el encabezado, los ítems
        con ícono, los números destacados y la foto. Se guarda todo junto con el botón de abajo.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />

      {message && (
        <div
          className={`mt-4 rounded-md p-3 text-sm border ${
            message.type === "success"
              ? "bg-primary-100 border-primary-200 text-primary-800"
              : "bg-accent-100 border-accent-200 text-accent-800"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mt-6 space-y-5 max-w-2xl">
        <section className="rounded-lg border border-background-200 bg-background-50 p-5 space-y-3">
          <h2 className="font-heading font-semibold text-foreground-900">Encabezado</h2>
          <div>
            <label className={labelCls}>Etiqueta pequeña (déjala vacía para ocultarla)</label>
            <input className={inputCls} value={cfg.etiqueta} onChange={(e) => set("etiqueta", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Título</label>
            <input className={inputCls} value={cfg.titulo} onChange={(e) => set("titulo", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Descripción (déjala vacía para ocultarla)</label>
            <textarea
              className={inputCls}
              rows={3}
              value={cfg.descripcion}
              onChange={(e) => set("descripcion", e.target.value)}
            />
          </div>
        </section>

        <ItemsEditor
          title="Ítems con ícono"
          description="Los puntos con ícono que aparecen bajo el encabezado, en dos columnas."
          items={benefits}
          onChange={setBenefits}
          fields={BENEFIT_FIELDS}
          itemName="Ítem"
          addLabel="+ Agregar ítem"
          newItem={() => ({ id: newItemId(), titulo: "", descripcion: "", icono: "ri-checkbox-circle-line" })}
        />

        <ItemsEditor
          title="Números destacados"
          description="Los recuadros con un número grande (12 m², 2.000+ m², 24/7, Ruta 5) que aparecen arriba de la foto."
          items={stats}
          onChange={setStats}
          fields={STAT_FIELDS}
          itemName="Número"
          addLabel="+ Agregar número"
          newItem={() => ({ id: newItemId(), valor: "", etiqueta: "" })}
        />

        <section className="rounded-lg border border-background-200 bg-background-50 p-5 space-y-3">
          <h2 className="font-heading font-semibold text-foreground-900">Foto</h2>
          {cfg.imagen_url ? (
            effectiveImageFit(cfg) === "recortar" ? (
              <img src={normalizeMediaUrl(cfg.imagen_url)} alt="" className="w-full h-40 object-cover object-top rounded-md" />
            ) : (
              <img
                src={normalizeMediaUrl(cfg.imagen_url)}
                alt=""
                className="block w-full h-auto max-h-72 object-contain rounded-md bg-background-100"
              />
            )
          ) : (
            <div className="w-full h-40 rounded-md bg-background-200 flex items-center justify-center text-xs text-foreground-500">
              Sin foto: la sección se muestra sin imagen
            </div>
          )}
          <div className="flex gap-2">
            <input
              className={inputCls}
              placeholder="URL de la imagen"
              value={cfg.imagen_url}
              onChange={(e) => set("imagen_url", e.target.value)}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer"
            >
              {uploading ? "Subiendo..." : "Subir foto"}
            </button>
          </div>
          <p className="text-xs text-foreground-500">Vacía el campo de la URL si quieres quitar la foto.</p>
          <fieldset>
            <legend className={labelCls}>Cómo se muestra la foto en el sitio</legend>
            <div className="space-y-1.5">
              {(
                [
                  ["completa", "Completa, sin recortar (recomendado para fotos propias)"],
                  ["recortar", "Franja recortada (el diseño original)"],
                ] as const
              ).map(([value, text]) => (
                <label key={value} className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                  <input
                    type="radio"
                    name="imagen_ajuste"
                    className="accent-primary-500"
                    checked={effectiveImageFit(cfg) === value}
                    onChange={() => set("imagen_ajuste", value)}
                  />
                  {text}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label className={labelCls}>Descripción de la foto (para accesibilidad)</label>
            <input className={inputCls} value={cfg.imagen_alt} onChange={(e) => set("imagen_alt", e.target.value)} />
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
          >
            {saving ? "Guardando..." : "Guardar sección"}
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm("Se cargan el encabezado y la foto originales (los ítems y los números no cambian). Para aplicarlos tienes que presionar «Guardar sección». ¿Continuar?")) {
                setCfg(DEFAULT_BENEFITS_SECTION);
                setMessage(null);
              }
            }}
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            Restablecer encabezado y foto originales
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
